import { ApiError, httpClient } from "@/core/api/http-client";

/**
 * Authentication API — the same contract as the RSM portal backend
 * (shared RSM user management tables). Point VITE_API_BASE_URL at the
 * RSM/WMS API gateway.
 */

export interface AuthRole {
  id: string;
  code: string;
  name: string;
}

export interface AuthUser {
  id: string;
  pfNumber: string;
  name: string;
  email: string;
  shopId: string | null;
  /** Workshop hierarchy code (SSE-IC | SSE | JE | SUPER_ADMIN). */
  designation: string;
  /** WMS users.id of the supervising SSE (null for SSE-IC / SUPER_ADMIN / unassigned). */
  reportsToUserId: string | null;
  status: "ACTIVE";
  roles: AuthRole[];
  permissions: string[];
  isSuperAdmin: boolean;
}

export interface LoginResponse {
  accessToken: string;
  expiresIn: number; // seconds
  user: AuthUser;
}

export interface LoginRequest {
  pfNumber: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Normalised login failure carrying the backend's stable error code
 * (ACCOUNT_NOT_FOUND, BAD_PASSWORD, ACCOUNT_LOCKED, …) plus details.
 */
export interface LoginFailure {
  /** Backend error code; undefined when the request never reached the server. */
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  /** HTTP status; undefined on network failure. */
  status?: number;
}

/** Extract the stable error shape from an ApiError / network error. */
export function toLoginFailure(err: unknown): LoginFailure {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as Record<string, unknown>;
    const nested = (body.error ?? body) as Record<string, unknown>;
    return {
      status: err.status,
      code: typeof nested.code === "string" ? nested.code : undefined,
      message: typeof nested.message === "string" ? nested.message : undefined,
      details: (nested.details as Record<string, unknown> | undefined) ?? undefined,
    };
  }
  // fetch() network failure / abort — no HTTP response.
  return {};
}

export async function login(payload: LoginRequest): Promise<LoginResponse> {
  return httpClient.post<LoginResponse>("/auth/login", payload, { skipAuthRedirect: true });
}

/**
 * Server-side no-op for stateless JWT, but called anyway so the server can
 * record the logout event in its audit log. The frontend clears the local
 * session regardless of the outcome.
 */
export async function logout(): Promise<void> {
  try {
    await httpClient.post("/auth/logout", {}, { skipAuthRedirect: true });
  } catch {
    // best-effort — local session is cleared by the caller either way
  }
}

/** Re-fetch the current principal (after permission changes). */
export async function getMe(): Promise<AuthUser> {
  return httpClient.get<AuthUser>("/auth/me");
}

/**
 * Silent refresh — trades the HttpOnly `wms_refresh` cookie for a new access
 * token. Called by the http-client 401 interceptor. Uses `skipAuthRedirect`
 * so a failing refresh doesn't recurse.
 */
export async function refresh(): Promise<LoginResponse> {
  return httpClient.post<LoginResponse>("/auth/refresh", undefined, { skipAuthRedirect: true });
}

/**
 * Ask for a password reset link. The server answers the same generic message
 * whether or not the PF number has an account.
 */
export async function forgotPassword(pfNumber: string): Promise<{ message: string }> {
  return httpClient.post<{ message: string }>(
    "/auth/forgot-password",
    { pfNumber },
    { skipAuthRedirect: true },
  );
}

/** Set a new password with the token from an emailed reset or setup link. */
export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  return httpClient.post<{ message: string }>(
    "/auth/reset-password",
    { token, password },
    { skipAuthRedirect: true },
  );
}

/**
 * IRWMS single sign-on: exchange the verified Keycloak access token for a WMS
 * session. Same response shape as /auth/login; the backend also sets the
 * refresh cookie. skipAuthRedirect so a 403 (no WMS access) is shown by the
 * callback page rather than bouncing to /login.
 */
export async function ssoExchange(irwmsAccessToken: string): Promise<LoginResponse> {
  return httpClient.post<LoginResponse>("/auth/sso", undefined, {
    skipAuthRedirect: true,
    headers: { Authorization: "Bearer " + irwmsAccessToken },
  });
}
